import {
	ArrowLeftIcon,
	ArrowRightIcon,
	ArrowUpRightIcon,
	Bars3Icon,
	ChevronDownIcon,
	ChevronRightIcon,
	ChevronUpIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import { Link, NavLink } from '@remix-run/react';
import { GQLCategoryFragment } from '@theprintbar/graphql';
import { ImageSize, WebRoutes, cn, useDebounce, useIsMobile, useOutsideClick } from '@theprintbar/shared';
import {
	Accordion,
	ButtonSize,
	HoverIcon,
	ImageContainer,
	OutlineAnchorButton,
	OutlineLinkButton,
	PrimaryLinkButton,
	PromoImageContainer,
	RichText,
	SmallArrowIcon,
	TransparentLinkButton,
} from '@theprintbar/ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Socials } from '~/components/Socials';
import { RootLoaderData } from '~/root';

interface MegaMenuProps {
	data: RootLoaderData;
	active?: boolean;
	setActive: (open: boolean) => void;
	navRef: React.RefObject<HTMLDivElement>;
	className?: string;
	categories: GQLCategoryFragment[];
}

export function MegaMenu({ data, active, setActive, navRef, categories, className }: MegaMenuProps) {
	const { globalContent } = data;
	const sortedMenu = globalContent.megaSubMenus.structure?.sort((a, b) => (a?.order || 0) - (b?.order || 0)) || [];
	const isMobile = useIsMobile();
	const [activeMenuIndex, setActiveMenuIndex] = useState(-1);
	const defaultMenu = isMobile ? sortedMenu[-1] : sortedMenu[0];
	const activeMenu = activeMenuIndex > -1 ? sortedMenu[activeMenuIndex] : defaultMenu;
	const buttonRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	const [debouncedOpen] = useDebounce(open, 100, { onlyDebounceWhenFalse: true });
	const [usingKeyboard, setUsingKeyboard] = useState(false);
	const topCategories = categories?.filter(c => !c.parents?.length).sort((a, b) => a.order - b.order);

	useOutsideClick(navRef, () => setOpen(false));

	useEffect(() => {
		if (!debouncedOpen) return;
		setActive(true);
	}, [debouncedOpen]);

	useEffect(() => {
		if (!active) {
			setOpen(false);
		}
	}, [active]);
	// keyboard modality + escape
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (['Tab', 'ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) setUsingKeyboard(true);
			if (e.key === 'Escape') setOpen(false);
		};
		const onMouse = () => setUsingKeyboard(false);
		window.addEventListener('keydown', onKey);
		window.addEventListener('mousedown', onMouse);
		return () => {
			window.removeEventListener('keydown', onKey);
			window.removeEventListener('mousedown', onMouse);
		};
	}, []);

	useEffect(() => {
		const el = panelRef.current;
		if (!el) return;
		if (active) {
			el.removeAttribute('inert');
		} else {
			el.setAttribute('inert', '');
		}
	}, [active]);

	// move focus to first link when opening via keyboard
	const openAndFocusFirst = useCallback(() => {
		if (isMobile) return;
		setOpen(true);
		requestAnimationFrame(() => {
			const first = panelRef.current?.querySelector(`a`);
			(first as HTMLElement | null)?.focus();
		});
	}, [isMobile]);

	function onTriggerKeyDown(e: React.KeyboardEvent) {
		switch (e.key) {
			case 'ArrowDown':
			case 'Enter':
			case ' ': // space
				e.preventDefault();
				if (!debouncedOpen) openAndFocusFirst();
				else setOpen(false);
				break;
		}
	}

	return (
		<div
			className={className}
			onMouseLeave={e => {
				setOpen(false);
			}}
		>
			<button
				ref={buttonRef}
				aria-haspopup="true"
				aria-expanded={debouncedOpen}
				aria-controls={`panel-mega`}
				onKeyDown={onTriggerKeyDown}
				onPointerEnter={e => {
					if (e.pointerType === 'touch') return;
					setOpen(true);
				}}
				onTouchStart={() => setOpen(!debouncedOpen)}
				className={cn(
					'h-full inline-flex transition-all font-semibold font-belfast text-sm items-center gap-1 px-2 lg:pr-4',
					usingKeyboard && 'focus:outline-none focus-visible:underline',
					debouncedOpen && 'lg:bg-blue/10',
				)}
			>
				<div className="flex items-center text-xs lg:text-sm  transition-all">
					{isMobile && debouncedOpen ? (
						<XMarkIcon className="h-6 w-6 lg:h-4 lg:w-4" />
					) : !isMobile && debouncedOpen ? (
						<ChevronUpIcon className="h-6 w-6 lg:h-4 lg:w-4" />
					) : (
						<Bars3Icon className="h-6 w-6 lg:w-4 lg:h-4" />
					)}
					<p className="hidden lg:block ml-2 text-inherit uppercase font-semibold font-belfast">Menu</p>
				</div>
			</button>

			<div
				ref={panelRef}
				id={`panel-mega`}
				role="region"
				aria-label="Mega menu"
				aria-hidden={!debouncedOpen}
				className={cn(
					'transition absolute inset-x-0 top-full z-40',
					'border-y border-blue bg-white-pearl shadow-lg',
					debouncedOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
				)}
			>
				<div className="absolute right-8 top-3 hidden xl:block">
					<button onClick={() => setOpen(false)} className="p-1 rounded-full border border-blue">
						<XMarkIcon className="w-5 h-5" />
					</button>
				</div>
				{/* ********** Desktop ********** */}
				<div className="hidden lg:flex h-full p-8">
					{/* ===== LEFT MENU ===== */}
					<div className="border border-blue rounded-2xl h-min relative bg-white p-8 min-w-64 mb-48">
						<div className="absolute flex items-center top-0 -translate-y-1/2 right-4 rounded-full border border-blue bg-yellow uppercase px-3 py-1 font-belfast text-xs font-bold">
							<h5 className="text-xs">Hover for more</h5>
							<HoverIcon className="h-3 w-3 ml-1" />
						</div>
						<div className="flex flex-col h-min">
							{/* primary links */}
							<div className="flex flex-col gap-2 font-display font-light text-xl">
								{sortedMenu.map((nav, index) => {
									const { link, title, subLinks } = nav || {};
									const active = (activeMenuIndex === -1 && !index) || activeMenuIndex === index;
									return link ? (
										<NavLink
											key={index}
											onMouseEnter={() => setActiveMenuIndex(index)}
											onClick={event => {
												event.preventDefault();
												setActiveMenuIndex(index);
											}}
											to="#"
											className={cn(
												'transition-all flex items-center py-1 whitespace-nowrap',
												active && 'opacity-100',
												activeMenu && !active && 'opacity-30',
											)}
										>
											{title}
											<ArrowRightIcon className={cn('w-4 h-4 ml-2 opacity-0', active && 'opacity-100')} />
										</NavLink>
									) : null;
								})}
							</div>

							{/* secondary links */}
							<div className="flex flex-col gap-1 text-xs font-normal mt-7 mb-4">
								{globalContent.megaSmallLinks.structure?.map((link, index) =>
									!link || !link.to ? null : (
										<NavLink key={`${link.to}${index}`} to={link.to} className="py-0.5">
											{link?.text}
										</NavLink>
									),
								)}
							</div>
							{/* Socials */}
							<div>
								<Socials data={data} colour="blue" />
							</div>
						</div>
					</div>
					<div className="flex flex-wrap">
						{/* ===== RIGHT MENU ===== */}
						{!!activeMenu && (
							<div className="w-full 2xl:w-1/4 py-8 px-4 lg:px-12 xl:px-20 h-fit">
								<h2 className="text-3xl">{!!activeMenu.altTitle ? activeMenu.altTitle : activeMenu.title}</h2>
								<div className="my-4 text-base">
									<p>{activeMenu.description}</p>
								</div>
								<OutlineAnchorButton
									className="w-fit uppercase font-belfast !font-bold text-xs"
									size={ButtonSize.S}
									icon={SmallArrowIcon}
									href={activeMenu.link || '#'}
								>
									See Overview
								</OutlineAnchorButton>
							</div>
						)}
						{/* SubLinks */}
						{sortedMenu.map((nav, index) =>
							!!nav?.subLinks?.length ? (
								<div
									className={cn(
										'w-full 2xl:w-3/4 py-8 px-4 lg:px-12 xl:px-20 flex flex-wrap h-fit',
										activeMenuIndex === -1 && index !== 0 && 'hidden',
										activeMenuIndex > -1 && activeMenuIndex !== index && 'hidden',
									)}
									key={`sub-${index}`}
								>
									{nav.subLinks.map(cta =>
										!cta?.links?.[0]?.to ? null : (
											<div key={`${index}-${cta.title}`} className="h-36 xl:h-28 flex gap-4 w-1/2 pr-4 mb-4 lg:mb-8">
												<Link to={cta.links[0].to} className="w-1/3 xl:w-auto">
													<ImageContainer
														size={ImageSize.Medium}
														image={cta.image}
														lazyLoad={false}
														className={cn(
															'rounded-2xl h-full aspect-square w-full xl:w-auto transition duration-200 ease-in-out hover:scale-110',
														)}
													/>
												</Link>
												<div className="w-2/3 xl:flex-1 h-full">
													<div className="flex-grow flex flex-col justify-between gap-4 h-full w-full aspect-[5/2] lg:w-auto lg:aspect-auto">
														{/* Text */}
														<div className="space-y-0 text-sm font-bold">
															<Link to={cta.links[0].to} className="hover:underline">
																<h4 className="text-sm uppercase">{cta.title}</h4>
															</Link>
															<RichText className="space-y-2" {...cta.text} />
														</div>
														{/* Button */}
														<div className={cn('flex flex-wrap gap-4 mt-auto')}>
															{cta?.links.map(
																(link, linkIndex) =>
																	link?.to && (
																		<OutlineAnchorButton
																			className={cn(
																				'w-fit uppercase font-belfast !font-bold text-xs',
																				linkIndex === 1 && 'border-none',
																			)}
																			size={ButtonSize.XS}
																			icon={SmallArrowIcon}
																			href={link.to}
																			key={`${link.text}-${linkIndex}`}
																		>
																			{link.text}
																		</OutlineAnchorButton>
																	),
															)}
														</div>
													</div>
												</div>
											</div>
										),
									)}
								</div>
							) : null,
						)}
					</div>
				</div>
				{/* ********** Mobile ********** */}
				<div className="lg:hidden h-screen absolute inset-0  z-40">
					<div className="font-sans flex flex-col h-full p-4 max-h-[80vh] overflow-y-auto bg-white-pearl border-b border-blue">
						<div className={cn('flex justify-between mb-2')}>
							{globalContent.mainLinks.structure?.map(link => (
								<Link
									key={link?.to}
									to={link?.to || '#'}
									className="flex font-belfast text-xs items-center underline font-bold uppercase"
								>
									{link?.text}
								</Link>
							))}
						</div>
						<div className={cn('flex flex-col justify-around')}>
							{topCategories?.map(category => {
								const children = categories.filter(c => c.parents?.some(p => p.code === category.code));
								return (
									<Accordion
										className="border-none"
										title={category.name}
										key={category.slug}
										openIcon={ChevronDownIcon}
										closeIcon={ChevronUpIcon}
										contentClassName="mb-0"
									>
										<div className="pl-4">
											<Link to={WebRoutes.Category(category.slug)} className="block py-2 font-bold text-sm">
												All {category.name}
											</Link>
											{children.map(child => {
												const grandChildren = categories.filter(c => c.parents?.some(p => p.code === child.code));
												return grandChildren.length ? (
													<Accordion
														title={child.name}
														key={child.slug}
														openIcon={ChevronDownIcon}
														closeIcon={ChevronUpIcon}
														className="border-none text-sm"
														contentClassName="mb-0"
													>
														<div className="pl-4">
															<Link
																to={WebRoutes.Category(child.slug)}
																className="block py-2 font-bold text-sm"
															>
																All {child.name}
															</Link>
															{grandChildren.map(grandChild => (
																<Link
																	to={WebRoutes.Category(grandChild.slug)}
																	key={grandChild.slug}
																	className="block py-2 text-sm"
																>
																	{grandChild.name}
																</Link>
															))}
														</div>
													</Accordion>
												) : (
													<Link
														to={WebRoutes.Category(child.slug)}
														key={child.slug}
														className="block py-2 text-sm"
													>
														{child.name}
													</Link>
												);
											})}
										</div>
									</Accordion>
								);
							})}
						</div>

						<div className="bg-white border border-blue rounded-3xl mt-4 flex flex-col mb-4 p-4">
							{activeMenu ? (
								<>
									<div className="relative flex items-center justify-between">
										<button onClick={() => setActiveMenuIndex(-1)} className="flex items-center gap-1">
											<ArrowLeftIcon className="w-3.5 h-3.5" />
											<p className="text-xs underline">Back</p>
										</button>
										<h2>{!!activeMenu?.altTitle ? activeMenu.altTitle : activeMenu?.title}</h2>
										<TransparentLinkButton
											className="text-xs underline"
											size={ButtonSize.XS}
											icon={SmallArrowIcon}
											to={activeMenu?.link || '#'}
										>
											Overview
										</TransparentLinkButton>
									</div>
									<div className="bg-white space-y-2 mt-4 pb-4">
										<div className="mt-2 mb-4 text-sm border-b border-blue pb-2">
											<p>{activeMenu?.description}</p>
										</div>
										<div className="grid grid-cols-2 gap-2">
											{activeMenu?.subLinks?.map((cta, index) =>
												cta?.links?.[0]?.to ? (
													<Link
														to={cta.links[0].to}
														key={`${index}-${cta.title}`}
														className="flex flex-col items-center w-full"
													>
														<div className="rounded-xl overflow-hidden w-full aspect-square">
															<PromoImageContainer
																size={ImageSize.Small}
																promoImage={cta.image}
																className="w-full h-full"
															/>
														</div>
														<h4 className="text-xs uppercase mt-2 text-center">
															{cta.title}
															{/* <ArrowRightIcon className="w-3.5 h-3.5 ml-2" /> */}
														</h4>
														<RichText className="space-y-2 hidden lg:block" {...cta.text} />
													</Link>
												) : null,
											)}
										</div>
									</div>
								</>
							) : (
								<>
									{/* Menu */}
									<div className="flex flex-col space-y-4">
										{sortedMenu.map((nav, index) => {
											const { link, title, subLinks } = nav || {};
											return link ? (
												<button
													key={index}
													className="flex items-center justify-between"
													onClick={() => setActiveMenuIndex(index)}
												>
													<p className="text-lg font-display font-light">{title}</p>
													<ChevronRightIcon className="w-4 h-4" />
												</button>
											) : null;
										})}
									</div>
									{/* Small links */}
									<div className="flex underline gap-x-6 gap-y-2 flex-wrap mt-4">
										{globalContent.megaSmallLinks.structure?.map(link => (
											<NavLink key={link?.text} to={link?.to || '#'}>
												<h4 className="text-sm font-light ">{link?.text}</h4>
											</NavLink>
										))}
									</div>
								</>
							)}
						</div>
						{/* Socials */}
						<div className="flex flex-col items-center mt-auto">
							<Socials data={data} colour="blue" className="space-x-8" />
						</div>
					</div>
					{/* Panel Backdrop */}
					<div
						ref={panelRef}
						id={`panel-mega`}
						role="region"
						aria-label="Mega menu"
						aria-hidden={!debouncedOpen}
						onClick={() => setOpen(false)}
						className={cn(
							'transition bg-black/20 h-full w-full',
							debouncedOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
						)}
					/>
				</div>
			</div>
		</div>
	);
}

export default MegaMenu;
